import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import brandPortalAccountRoute from "@faire/web-api--source/routes/www/brand-portal/account";
import articlePage from "@faire/web-api--source/routes/www/support/articles/articleId";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

interface ITaxForm1099IAProps {
  brand: IBrand | undefined;
}

export const TaxForm1099IA: React.FC<ITaxForm1099IAProps> = ({ brand }) => {
  if (brand?.based_in === "USA") {
    return (
      <Column>
        <Typography>
          <StrictLocalMsg
            defaultMessage="In order to receive a 1099-K tax form you must meet <LinkFormatting>certain sales thresholds</LinkFormatting> on Faire. Once you confirm you meet those thresholds, you can view and download your 1099-K tax form, by following these steps:"
            values={{
              LinkFormatting: (chunks: JSX.Element) =>
                CertainSaleLinkFormatting(chunks),
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/dbba3377e441e6deed99387b00d76142325e354727b3ce8d7356c82ad66d8bba.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <ol>
          <LiItem step={1}>
            <Typography>
              <StrictLocalMsg
                defaultMessage="<BoldFormatting>For Electronic Delivery:</BoldFormatting>"
                values={{
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  img: "https://cdn.faire.com/fastly/dbba3377e441e6deed99387b00d76142325e354727b3ce8d7356c82ad66d8bba.png",
                }}
              />
            </Typography>
            <Typography>
              <StrictLocalMsg
                defaultMessage="If you opted for electronic delivery, you can find your 1099-K tax form in <BoldFormatting>Account settings</BoldFormatting> under the <LinkFormatting>Export & Taxes</LinkFormatting> page in the Faire portal. It will be available as soon as it’s ready, typically before January 31st."
                values={{
                  LinkFormatting: (chunks: JSX.Element) =>
                    PayoutsLinkFormatting(chunks, "export-taxes"),
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  img: "https://cdn.faire.com/fastly/dbba3377e441e6deed99387b00d76142325e354727b3ce8d7356c82ad66d8bba.png",
                }}
              />
            </Typography>
          </LiItem>
          <Spacer height="1x" />
          <LiItem step={2}>
            <Typography>
              <StrictLocalMsg
                defaultMessage="<BoldFormatting>For Physical Mail Delivery:</BoldFormatting>"
                values={{
                  BoldFormatting: (chunks: JSX.Element) =>
                    BoldFormatting(chunks),
                }}
                description={{
                  img: "https://cdn.faire.com/fastly/dbba3377e441e6deed99387b00d76142325e354727b3ce8d7356c82ad66d8bba.png",
                }}
              />
            </Typography>
            <Typography>
              <StrictLocalMsg
                defaultMessage="If you requested a physical copy, it should be delivered by mid-February. Ensure that your taxpayer details and legal address are up-to-date in your <LinkFormatting>Export & Taxes</LinkFormatting> settings to avoid any delays."
                values={{
                  LinkFormatting: (chunks: JSX.Element) =>
                    PayoutsLinkFormatting(chunks, "export-taxes"),
                }}
                description={{
                  img: "https://cdn.faire.com/fastly/dbba3377e441e6deed99387b00d76142325e354727b3ce8d7356c82ad66d8bba.png",
                }}
              />
            </Typography>
          </LiItem>
        </ol>
      </Column>
    );
  }
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="You won’t receive a 1099-K from Faire, as these are only sent to brands based in the US. If you are based in the US and believe that you should have received a form, please contact us below."
          description={{
            text: "A disclaimer for brands based outside the US.",
          }}
        />
      </Typography>
    </Column>
  );
};

const PayoutsLinkFormatting = (chunks: JSX.Element, tab: string) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalAccountRoute({ tab })}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const CertainSaleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={articlePage("360022603552")}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
